import { useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";
const kudozLogo = "/kudoz-icon-disc.png";

interface KudozCelebrationProps {
  show: boolean;
  amount: number;
  level: 'basic' | 'medium' | 'high';
  onComplete: () => void;
}

/**
 * Refined, adult-friendly celebration for review submissions
 * Scales animation sophistication with effort level
 */
export function KudozCelebration({ show, amount, level, onComplete }: KudozCelebrationProps) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const hasStartedRef = useRef(false);
  const duration = level === 'high' ? 2500 : level === 'medium' ? 2000 : 1500;

  useEffect(() => {
    if (show && !hasStartedRef.current) {
      hasStartedRef.current = true;
      timerRef.current = setTimeout(() => onCompleteRef.current(), duration);
    }
    if (!show) {
      hasStartedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [show, duration]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {level === 'high' && (
        <div className="absolute inset-0 bg-kudoz/10 backdrop-blur-sm" />
      )}

      <div className="pointer-events-auto bg-background border-2 border-kudoz/30 rounded-xl p-6 shadow-2xl max-w-sm mx-4 relative animate-in fade-in zoom-in-95 duration-300">
        {level === 'medium' && (
          <div className="absolute -inset-1 bg-gradient-radial from-kudoz/20 via-kudoz/10 to-transparent rounded-xl blur-md -z-10" />
        )}
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <img
              src={kudozLogo}
              alt="Kudoz"
              className={`w-16 h-16 ${level === 'high' ? 'animate-bounce' : level === 'medium' ? 'animate-pulse' : ''}`}
            />
            {level === 'high' && (
              <>
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute top-1/2 left-1/2 animate-ping"
                    style={{
                      marginLeft: '-4px',
                      marginTop: '-4px',
                      transform: `translate(${Math.cos(i * 60 * Math.PI / 180) * 40}px, ${Math.sin(i * 60 * Math.PI / 180) * 40}px)`,
                      animationDelay: `${i * 0.1}s`,
                      animationDuration: '1.2s',
                    }}
                  >
                    <Sparkles className="w-3 h-3 text-kudoz" fill="currentColor" />
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="text-center space-y-1">
            <h3 className="text-xl font-semibold animate-in fade-in slide-in-from-bottom-2 duration-300 delay-200">
              Review posted
            </h3>
            <p className="text-2xl font-bold text-kudoz animate-in fade-in slide-in-from-bottom-2 duration-300 delay-300">
              {amount} Kudoz earned
            </p>
            {level === 'high' && (
              <p className="text-sm text-muted-foreground animate-in fade-in duration-300 delay-400">
                Comprehensive review
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
